import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import { AuthContext } from '../../contexts/AuthContext'
import '../../css/surveyAnalytics.css'

const PALETTE = ['#7c5cff', '#22c7a9', '#ffb84d', '#ff6b81', '#47a7ff', '#c77dff', '#82d173', '#ff8f5c']
const DEFAULT_COLUMNS = ['submitted_at', 'frequency', 'satisfaction', 'favorite_host', 'favorite_program', 'city', 'age']
const MERGEABLE = ['favorite_host', 'favorite_program', 'favorite_commentator', 'city', 'profession']

const formatNumber = (value, digits = 0) => new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: digits,
  minimumFractionDigits: digits,
}).format(value ?? 0)

function csvCell(value) {
  const escaped = String(value ?? '').replace(/"/g, '""')
  return `"${escaped}"`
}

function downloadBlob(content, name, type) {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function LoadingState() {
  return (
    <div className="sa-state-card">
      <span className="sa-loader" />
      <strong>Sto leggendo il sondaggio</strong>
      <p>Profilazione delle risposte e rilevamento delle varianti in corso…</p>
    </div>
  )
}

function EmptyState({ title = 'Nessun dato nel perimetro', text = 'Prova a rimuovere uno o più filtri.' }) {
  return (
    <div className="sa-empty">
      <i className="fas fa-filter-circle-xmark" />
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  )
}

function KpiCard({ eyebrow, value, suffix, detail, icon, tone = 'violet' }) {
  return (
    <article className={`sa-kpi sa-kpi--${tone}`}>
      <div className="sa-kpi__icon"><i className={icon} /></div>
      <div>
        <span>{eyebrow}</span>
        <strong>{value}<small>{suffix}</small></strong>
        <p>{detail}</p>
      </div>
    </article>
  )
}

function HorizontalBars({ items, total, limit = 10, compact = false }) {
  const shown = items.slice(0, limit)
  const max = Math.max(...shown.map((item) => item.count), 1)
  if (!shown.length) return <EmptyState />
  return (
    <div className={`sa-bars ${compact ? 'sa-bars--compact' : ''}`}>
      {shown.map((item, index) => (
        <div className="sa-bar" key={`${item.label}-${index}`}>
          <div className="sa-bar__meta">
            <span title={item.label}>{item.label || 'Non indicato'}</span>
            <strong>{formatNumber(item.count)} <small>{total ? `${formatNumber((item.count / total) * 100, 1)}%` : ''}</small></strong>
          </div>
          <div className="sa-bar__track">
            <span style={{ width: `${(item.count / max) * 100}%`, background: PALETTE[index % PALETTE.length] }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function DonutChart({ items, total }) {
  const shown = items.slice(0, 7)
  const displayed = shown.reduce((sum, item) => sum + item.count, 0)
  const slices = [...shown]
  if (total > displayed) slices.push({ label: 'Altri', count: total - displayed })
  let cursor = 0
  const stops = slices.map((item, index) => {
    const from = cursor
    cursor += total ? (item.count / total) * 100 : 0
    return `${PALETTE[index % PALETTE.length]} ${from}% ${cursor}%`
  })
  if (!total) return <EmptyState />
  return (
    <div className="sa-donut-wrap">
      <div className="sa-donut" style={{ background: `conic-gradient(${stops.join(',')})` }}>
        <div><strong>{formatNumber(total)}</strong><span>risposte</span></div>
      </div>
      <div className="sa-legend">
        {slices.map((item, index) => (
          <div key={item.label}>
            <i style={{ background: PALETTE[index % PALETTE.length] }} />
            <span>{item.label}</span>
            <strong>{formatNumber((item.count / total) * 100, 1)}%</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

function TrendChart({ items }) {
  if (!items.length) return <EmptyState />
  const width = 760
  const height = 210
  const pad = 22
  const max = Math.max(...items.map((item) => item.count), 1)
  const points = items.map((item, index) => {
    const x = items.length === 1 ? width / 2 : pad + (index / (items.length - 1)) * (width - pad * 2)
    const y = height - pad - (item.count / max) * (height - pad * 2)
    return { ...item, x, y }
  })
  const line = points.map((point, index) => `${index ? 'L' : 'M'} ${point.x} ${point.y}`).join(' ')
  const area = `${line} L ${points.at(-1).x} ${height - pad} L ${points[0].x} ${height - pad} Z`
  return (
    <div className="sa-trend">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="Risposte nel tempo">
        <defs>
          <linearGradient id="saArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7c5cff" stopOpacity=".42" />
            <stop offset="100%" stopColor="#7c5cff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((ratio) => <line key={ratio} x1={pad} x2={width - pad} y1={height - pad - ratio * (height - pad * 2)} y2={height - pad - ratio * (height - pad * 2)} className="sa-grid-line" />)}
        <path d={area} fill="url(#saArea)" />
        <path d={line} className="sa-trend-line" />
        {points.map((point) => <circle key={point.label} cx={point.x} cy={point.y} r="4" className="sa-trend-dot"><title>{point.label}: {point.count}</title></circle>)}
      </svg>
      <div className="sa-trend-labels">
        {points.filter((_, index) => items.length <= 9 || index % Math.ceil(items.length / 7) === 0 || index === items.length - 1).map((point) => (
          <span key={point.label}>{point.label.slice(5).split('-').reverse().join('/')}</span>
        ))}
      </div>
    </div>
  )
}

function SegmentedBars({ items, segmentLabels, total }) {
  if (!items.length) return <EmptyState />
  return (
    <div className="sa-segmented">
      <div className="sa-segmented__legend">
        {segmentLabels.slice(0, 8).map((label, index) => <span key={label}><i style={{ background: PALETTE[index % PALETTE.length] }} />{label}</span>)}
      </div>
      {items.slice(0, 10).map((item) => {
        const rowTotal = Object.values(item.segments).reduce((sum, count) => sum + count, 0)
        return (
          <div className="sa-segment-row" key={item.label}>
            <div><span title={item.label}>{item.label}</span><strong>{formatNumber(item.count)} <small>{formatNumber((item.count / total) * 100, 1)}%</small></strong></div>
            <div className="sa-segment-track">
              {segmentLabels.slice(0, 8).map((label, index) => item.segments[label] ? (
                <span key={label} title={`${label}: ${item.segments[label]}`} style={{ width: `${(item.segments[label] / rowTotal) * 100}%`, background: PALETTE[index % PALETTE.length] }} />
              ) : null)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ScoreCard({ label, value, distribution, sample }) {
  const positive = distribution.filter((item) => Number(item.label) >= 8).reduce((sum, item) => sum + item.count, 0)
  return (
    <article className="sa-score">
      <div className="sa-score__head"><span>{label}</span><strong>{formatNumber(value, 1)}<small>/10</small></strong></div>
      <div className="sa-score__gauge"><span style={{ width: `${value * 10}%` }} /></div>
      <p><b>{sample ? formatNumber((positive / sample) * 100, 1) : 0}%</b> ha dato un voto 8–10</p>
    </article>
  )
}

function Panel({ title, eyebrow, action, children, className = '' }) {
  return (
    <section className={`sa-panel ${className}`}>
      <header className="sa-panel__head">
        <div>{eyebrow && <span>{eyebrow}</span>}<h2>{title}</h2></div>
        {action}
      </header>
      {children}
    </section>
  )
}

export default function SurveyAnalyticsPage() {
  const { hasPermission } = useContext(AuthContext)
  const [dataset, setDataset] = useState(null)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('overview')
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [filters, setFilters] = useState([])
  const [filterField, setFilterField] = useState('frequency')
  const [filterValue, setFilterValue] = useState('')
  const [dimension, setDimension] = useState('favorite_host')
  const [segment, setSegment] = useState('')
  const [chartType, setChartType] = useState('bars')
  const [explorerSearch, setExplorerSearch] = useState('')
  const [chartLimit, setChartLimit] = useState('25')
  const [exactMerge, setExactMerge] = useState(true)
  const [entityMerge, setEntityMerge] = useState(true)
  const [acceptedFuzzy, setAcceptedFuzzy] = useState(() => new Set())
  const [qualityField, setQualityField] = useState('favorite_host')
  const [qualitySearch, setQualitySearch] = useState('')
  const [selectedColumns, setSelectedColumns] = useState(() => new Set(DEFAULT_COLUMNS))
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [sort, setSort] = useState({ field: 'timestamp', direction: 'desc' })
  const [page, setPage] = useState(1)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState(null)
  const pageSize = 25
  const canManageDataset = hasPermission('survey_analytics_manage')

  useEffect(() => {
    let active = true
    api.get('/survey-analytics/dataset')
      .then(({ data }) => {
        if (!active) return
        setDataset(data)
        setDateFrom(data.summary.dateFrom ?? '')
        setDateTo(data.summary.dateTo ?? '')
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message ?? 'Impossibile caricare il sondaggio.')
      })
    return () => { active = false }
  }, [])

  const fieldMap = useMemo(() => new Map((dataset?.fields ?? []).map((field) => [field.id, field])), [dataset])

  const exactMaps = useMemo(() => {
    const maps = new Map()
    for (const field of dataset?.fields ?? []) {
      const map = new Map()
      for (const group of field.profile.exactGroups) {
        for (const variant of group.variants) map.set(variant.value, group.canonical)
      }
      maps.set(field.id, map)
    }
    return maps
  }, [dataset])

  const fuzzyMaps = useMemo(() => {
    const maps = new Map()
    for (const field of dataset?.fields ?? []) {
      const map = new Map()
      for (const suggestion of field.profile.fuzzySuggestions) {
        if (acceptedFuzzy.has(suggestion.id)) map.set(suggestion.from, suggestion.to)
      }
      maps.set(field.id, map)
    }
    return maps
  }, [dataset, acceptedFuzzy])

  const normalize = useCallback((fieldId, raw) => {
    let value = String(raw ?? '').trim()
    if (exactMerge) value = exactMaps.get(fieldId)?.get(value) ?? value
    const fuzzy = fuzzyMaps.get(fieldId)
    for (let guard = 0; guard < 4 && fuzzy?.has(value); guard++) value = fuzzy.get(value)
    return value
  }, [exactMerge, exactMaps, fuzzyMaps])

  const valuesFor = useCallback((row, fieldId) => {
    const field = fieldMap.get(fieldId)
    const raw = String(row?.[fieldId] ?? '')
    if (!raw) return []
    if (entityMerge && MERGEABLE.includes(fieldId)) {
      const entities = field?.profile.entityModel?.matches?.[raw]
      if (entities?.length) return entities
    }
    return (field?.kind === 'multi' ? raw.split(';') : [raw]).map((value) => normalize(fieldId, value)).filter(Boolean)
  }, [fieldMap, normalize, entityMerge])

  const filterGroups = useMemo(() => {
    const groups = new Map()
    for (const filter of filters) {
      const values = groups.get(filter.field) ?? []
      values.push(filter.value)
      groups.set(filter.field, values)
    }
    return [...groups.entries()].map(([field, values]) => ({ field, values }))
  }, [filters])

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('it')
    return (dataset?.rows ?? []).filter((row) => {
      if (dateFrom && row.dateKey < dateFrom) return false
      if (dateTo && row.dateKey > dateTo) return false
      if (needle && !dataset.fields.some((field) => String(row[field.id] ?? '').toLocaleLowerCase('it').includes(needle))) return false
      return filterGroups.every((group) => {
        const rowValues = valuesFor(row, group.field)
        return group.values.some((value) => rowValues.includes(value))
      })
    })
  }, [dataset, dateFrom, dateTo, search, filterGroups, valuesFor])

  useEffect(() => { setPage(1) }, [search, dateFrom, dateTo, filters, sort])

  const distribution = useCallback((rows, fieldId) => {
    const counts = new Map()
    for (const row of rows) for (const value of valuesFor(row, fieldId)) counts.set(value, (counts.get(value) ?? 0) + 1)
    const field = fieldMap.get(fieldId)
    return [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => {
      if (field?.kind === 'rating') return Number(a.label) - Number(b.label)
      return b.count - a.count || a.label.localeCompare(b.label, 'it')
    })
  }, [fieldMap, valuesFor])

  const average = useCallback((fieldId) => {
    const values = filteredRows.filter((row) => String(row[fieldId] ?? '').trim() !== '').map((row) => Number(row[fieldId])).filter(Number.isFinite)
    return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0
  }, [filteredRows])

  const trend = useMemo(() => {
    const counts = new Map()
    for (const row of filteredRows) if (row.dateKey) counts.set(row.dateKey, (counts.get(row.dateKey) ?? 0) + 1)
    return [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredRows])

  const selectedDistribution = useMemo(() => distribution(filteredRows, dimension), [distribution, filteredRows, dimension])
  const searchedDistribution = useMemo(() => {
    const needle = explorerSearch.trim().toLocaleLowerCase('it')
    return needle ? selectedDistribution.filter((item) => item.label.toLocaleLowerCase('it').includes(needle)) : selectedDistribution
  }, [selectedDistribution, explorerSearch])
  const chartDistribution = chartLimit === 'all' ? searchedDistribution : searchedDistribution.slice(0, Number(chartLimit))
  const selectedAnswerTotal = selectedDistribution.reduce((sum, item) => sum + item.count, 0)

  const segmented = useMemo(() => {
    if (!segment) return { items: [], labels: [] }
    const segmentTotals = distribution(filteredRows, segment)
    const labels = segmentTotals.slice(0, 8).map((item) => item.label)
    const matrix = new Map()
    for (const row of filteredRows) {
      const dimensions = valuesFor(row, dimension)
      const segments = valuesFor(row, segment).filter((value) => labels.includes(value))
      for (const value of dimensions) {
        const entry = matrix.get(value) ?? { label: value, count: 0, segments: {} }
        entry.count++
        for (const segmentValue of segments) entry.segments[segmentValue] = (entry.segments[segmentValue] ?? 0) + 1
        matrix.set(value, entry)
      }
    }
    return { items: [...matrix.values()].sort((a, b) => b.count - a.count), labels }
  }, [segment, distribution, filteredRows, valuesFor, dimension])

  const completion = useMemo(() => {
    if (!filteredRows.length || !dataset) return 0
    const answered = filteredRows.reduce((total, row) => total + dataset.fields.filter((field) => row[field.id]).length, 0)
    return (answered / (filteredRows.length * dataset.fields.length)) * 100
  }, [filteredRows, dataset])

  const quality = fieldMap.get(qualityField)
  const fuzzyVisible = (quality?.profile.fuzzySuggestions ?? []).filter((suggestion) => {
    const needle = qualitySearch.toLocaleLowerCase('it')
    return !needle || suggestion.from.toLocaleLowerCase('it').includes(needle) || suggestion.to.toLocaleLowerCase('it').includes(needle)
  })
  const qualityOptionsVisible = (quality?.profile.options ?? []).filter((option) => {
    const normalized = valuesFor({ [qualityField]: option.value }, qualityField).join(' · ')
    const needle = qualitySearch.trim().toLocaleLowerCase('it')
    return !needle || option.value.toLocaleLowerCase('it').includes(needle) || normalized.toLocaleLowerCase('it').includes(needle)
  })

  const filterOptions = useMemo(() => distribution(dataset?.rows ?? [], filterField), [distribution, dataset, filterField])

  const addFilter = () => {
    if (!filterField || !filterValue || filters.some((item) => item.field === filterField && item.value === filterValue)) return
    setFilters((current) => [...current, { field: filterField, value: filterValue }])
    setFilterValue('')
  }

  const toggleFuzzy = (id) => setAcceptedFuzzy((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const toggleColumn = (id) => setSelectedColumns((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const exportRows = () => {
    const fields = dataset.fields.filter((field) => selectedColumns.has(field.id))
    const lines = [fields.map((field) => csvCell(field.shortLabel)).join(';')]
    for (const row of filteredRows) lines.push(fields.map((field) => {
      if (field.kind === 'multi' || MERGEABLE.includes(field.id)) return csvCell(valuesFor(row, field.id).join('; '))
      return csvCell(normalize(field.id, row[field.id]))
    }).join(';'))
    downloadBlob(`\ufeff${lines.join('\n')}`, `radio-radio-2026-filtrato-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv;charset=utf-8')
  }

  const uploadDataset = async (event) => {
    event.preventDefault()
    if (!uploadFile || uploading) return
    if (uploadFile.size > 10 * 1024 * 1024) {
      setUploadMessage({ type: 'error', text: 'Il CSV supera il limite di 10 MB.' })
      return
    }

    setUploading(true)
    setUploadMessage({ type: 'info', text: 'Validazione dello storico e dei nuovi record in corso…' })
    try {
      const form = new FormData()
      form.append('file', uploadFile)
      const { data } = await api.post('/survey-analytics/upload', form)
      setDataset(data.dataset)
      setDateFrom(data.dataset.summary.dateFrom ?? '')
      setDateTo(data.dataset.summary.dateTo ?? '')
      setFilters([])
      setSearch('')
      setAcceptedFuzzy(new Set())
      setUploadFile(null)
      setUploadMessage({
        type: 'ok',
        text: `Dataset aggiornato: ${formatNumber(data.addedResponses)} nuovi record, ${formatNumber(data.dataset.summary.responses)} totali. Backup creato.`,
      })
    } catch (uploadError) {
      setUploadMessage({
        type: 'error',
        text: uploadError?.response?.data?.message ?? 'Aggiornamento non riuscito. Il dataset precedente è rimasto attivo.',
      })
    } finally {
      setUploading(false)
    }
  }

  const sortedRows = useMemo(() => [...filteredRows].sort((a, b) => {
    const left = String(a[sort.field] ?? '')
    const right = String(b[sort.field] ?? '')
    return left.localeCompare(right, 'it', { numeric: true }) * (sort.direction === 'asc' ? 1 : -1)
  }), [filteredRows, sort])
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize))
  const visibleRows = sortedRows.slice((page - 1) * pageSize, page * pageSize)
  const visibleFields = dataset?.fields.filter((field) => selectedColumns.has(field.id)) ?? []

  if (error) return <main className="sa-shell"><div className="sa-state-card sa-state-card--error"><i className="fas fa-triangle-exclamation" /><strong>Dataset non disponibile</strong><p>{error}</p></div></main>
  if (!dataset) return <main className="sa-shell"><LoadingState /></main>

  const activeFilterCount = filters.length + Boolean(search) + Boolean(dateFrom !== dataset.summary.dateFrom || dateTo !== dataset.summary.dateTo)

  return (
    <main className="sa-shell">
      <header className="sa-hero">
        <div className="sa-hero__copy">
          <span className="sa-overline"><i className="fas fa-chart-pie" /> Audience Intelligence</span>
          <h1>Radio Radio <em>2026</em></h1>
          <p>Esplora il sondaggio, segmenta il pubblico e ripulisci le risposte aperte senza modificare la fonte.</p>
        </div>
        <div className="sa-hero__meta">
          <span><i className="fas fa-file-csv" /> {dataset.source.name}</span>
          <span><i className="fas fa-clock" /> aggiornato {new Date(dataset.source.modifiedAt).toLocaleString('it-IT')}</span>
          <button onClick={exportRows}><i className="fas fa-download" /> Esporta vista</button>
          {canManageDataset && <button className="sa-upload-trigger" onClick={() => { setUploadOpen(true); setUploadMessage(null) }}><i className="fas fa-cloud-arrow-up" /> Aggiorna CSV</button>}
        </div>
      </header>

      {uploadOpen && (
        <div className="sa-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !uploading) setUploadOpen(false) }}>
          <section className="sa-upload-modal" role="dialog" aria-modal="true" aria-labelledby="survey-upload-title">
            <header><div><span className="sa-overline"><i className="fas fa-shield-halved" /> Aggiornamento controllato</span><h2 id="survey-upload-title">Carica il CSV aggiornato</h2></div><button onClick={() => setUploadOpen(false)} disabled={uploading} aria-label="Chiudi"><i className="fas fa-xmark" /></button></header>
            <p>Il file deve avere le stesse 26 colonne e contenere tutto lo storico attuale più gli eventuali nuovi record. Prima della sostituzione viene creato un backup; programmi, nomi, città e professioni saranno rivalidati con le regole consolidate.</p>
            <form onSubmit={uploadDataset}>
              <label className="sa-upload-drop">
                <input type="file" accept=".csv,text/csv" onChange={(event) => { setUploadFile(event.target.files?.[0] ?? null); setUploadMessage(null) }} disabled={uploading} />
                <i className="fas fa-file-csv" />
                <span><strong>{uploadFile?.name ?? 'Scegli il nuovo CSV'}</strong><small>{uploadFile ? `${formatNumber(uploadFile.size / 1024, 1)} KB` : 'Solo CSV · massimo 10 MB'}</small></span>
              </label>
              <div className="sa-upload-rules"><span><i className="fas fa-check" /> Schema identico</span><span><i className="fas fa-check" /> Storico preservato</span><span><i className="fas fa-check" /> Backup automatico</span></div>
              {uploadMessage && <div className={`sa-upload-message sa-upload-message--${uploadMessage.type}`}><i className={`fas ${uploadMessage.type === 'ok' ? 'fa-circle-check' : uploadMessage.type === 'error' ? 'fa-triangle-exclamation' : 'fa-spinner fa-spin'}`} />{uploadMessage.text}</div>}
              <footer><button type="button" onClick={() => setUploadOpen(false)} disabled={uploading}>Chiudi</button><button type="submit" disabled={!uploadFile || uploading}><i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'}`} />{uploading ? 'Validazione…' : 'Valida e aggiorna'}</button></footer>
            </form>
          </section>
        </div>
      )}

      <section className="sa-toolbar">
        <div className="sa-search">
          <i className="fas fa-magnifying-glass" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca in tutte le risposte…" />
          {search && <button onClick={() => setSearch('')} aria-label="Cancella ricerca"><i className="fas fa-xmark" /></button>}
        </div>
        <label><span>Dal</span><input type="date" min={dataset.summary.dateFrom} max={dateTo || dataset.summary.dateTo} value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
        <label><span>Al</span><input type="date" min={dateFrom || dataset.summary.dateFrom} max={dataset.summary.dateTo} value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        <button className="sa-reset" disabled={!activeFilterCount} onClick={() => { setSearch(''); setFilters([]); setDateFrom(dataset.summary.dateFrom); setDateTo(dataset.summary.dateTo) }}>
          <i className="fas fa-rotate-left" /> Reset {activeFilterCount ? `(${activeFilterCount})` : ''}
        </button>
      </section>

      <section className="sa-filter-builder">
        <div><i className="fas fa-sliders" /><strong>Filtro avanzato</strong><small>OR nello stesso campo · AND tra campi</small></div>
        <select value={filterField} onChange={(event) => { setFilterField(event.target.value); setFilterValue('') }}>
          {dataset.fields.filter((field) => field.kind !== 'date' && field.id !== 'improvements').map((field) => <option key={field.id} value={field.id}>{field.shortLabel}</option>)}
        </select>
        <select value={filterValue} onChange={(event) => setFilterValue(event.target.value)}>
          <option value="">Scegli un valore…</option>
          {filterOptions.map((item) => <option key={item.label} value={item.label}>{item.label} ({item.count})</option>)}
        </select>
        <button onClick={addFilter} disabled={!filterValue}><i className="fas fa-plus" /> Aggiungi</button>
        <div className="sa-filter-chips">
          {filters.map((filter, index) => <button key={`${filter.field}-${filter.value}`} onClick={() => setFilters((current) => current.filter((_, itemIndex) => itemIndex !== index))}><b>{fieldMap.get(filter.field)?.shortLabel}</b>: {filter.value}<i className="fas fa-xmark" /></button>)}
        </div>
      </section>

      <nav className="sa-tabs" aria-label="Sezioni analisi">
        {[
          ['overview', 'Panoramica', 'fa-gauge-high'],
          ['explorer', 'Explorer', 'fa-chart-column'],
          ['quality', 'Qualità dati', 'fa-wand-magic-sparkles'],
          ['records', 'Risposte', 'fa-table'],
        ].map(([id, label, icon]) => <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}><i className={`fas ${icon}`} />{label}{id === 'quality' && <span>{dataset.summary.exactMergeGroups + dataset.summary.fuzzySuggestions}</span>}</button>)}
      </nav>

      <div className="sa-result-strip">
        <strong>{formatNumber(filteredRows.length)}</strong><span>di {formatNumber(dataset.summary.responses)} risposte nel perimetro</span>
        <div><i style={{ width: `${(filteredRows.length / dataset.summary.responses) * 100}%` }} /></div>
      </div>

      {tab === 'overview' && (
        <div className="sa-view">
          <section className="sa-kpi-grid">
            <KpiCard eyebrow="Risposte analizzate" value={formatNumber(filteredRows.length)} detail={`${dataset.summary.dateFrom.split('-').reverse().join('/')} — ${dataset.summary.dateTo.split('-').reverse().join('/')}`} icon="fas fa-users" tone="violet" />
            <KpiCard eyebrow="Completezza media" value={formatNumber(completion, 1)} suffix="%" detail={`${dataset.summary.fields} domande profilate`} icon="fas fa-circle-check" tone="green" />
            <KpiCard eyebrow="Soddisfazione" value={formatNumber(average('satisfaction'), 1)} suffix="/10" detail="media sul perimetro attivo" icon="fas fa-face-smile" tone="amber" />
            <KpiCard eyebrow="Qualità da revisionare" value={dataset.summary.fuzzySuggestions} detail={`${dataset.summary.exactMergeGroups} gruppi automatici rilevati`} icon="fas fa-wand-magic-sparkles" tone="blue" />
          </section>

          <div className="sa-grid sa-grid--wide">
            <Panel eyebrow="Raccolta" title="Andamento delle risposte" className="sa-panel--trend" action={<span className="sa-panel-stat"><b>{trend.length}</b> giorni attivi</span>}>
              <TrendChart items={trend} />
            </Panel>
            <Panel eyebrow="Audience" title="Frequenza di ascolto">
              <DonutChart items={distribution(filteredRows, 'frequency')} total={filteredRows.length} />
            </Panel>
          </div>

          <Panel eyebrow="Brand health" title="Le tre dimensioni della relazione">
            <div className="sa-scores">
              {['satisfaction', 'closeness', 'trust'].map((id) => <ScoreCard key={id} label={fieldMap.get(id)?.shortLabel} value={average(id)} distribution={distribution(filteredRows, id)} sample={filteredRows.length} />)}
            </div>
          </Panel>

          <div className="sa-grid sa-grid--three">
            <Panel eyebrow="Contenuti" title="Programmi preferiti"><HorizontalBars items={distribution(filteredRows, 'favorite_program')} total={filteredRows.length} limit={7} compact /></Panel>
            <Panel eyebrow="Profilo" title="Fasce d’età"><HorizontalBars items={distribution(filteredRows, 'age')} total={filteredRows.length} limit={7} compact /></Panel>
            <Panel eyebrow="Distribuzione" title="Città principali"><HorizontalBars items={distribution(filteredRows, 'city')} total={filteredRows.length} limit={7} compact /></Panel>
          </div>

          <div className="sa-grid sa-grid--half">
            <Panel eyebrow="Touchpoint" title="Dove viene ascoltata"><HorizontalBars items={distribution(filteredRows, 'listening_devices')} total={filteredRows.length} limit={8} /></Panel>
            <Panel eyebrow="Commerciale" title="Partner più ricordati"><HorizontalBars items={distribution(filteredRows, 'remembered_partners')} total={filteredRows.length} limit={8} /></Panel>
          </div>
        </div>
      )}

      {tab === 'explorer' && (
        <div className="sa-view">
          <section className="sa-explorer-controls">
            <label><span>Analizza</span><select value={dimension} onChange={(event) => { setDimension(event.target.value); setExplorerSearch('') }}>{dataset.fields.filter((field) => !['date', 'open'].includes(field.kind) || MERGEABLE.includes(field.id)).map((field) => <option key={field.id} value={field.id}>{field.shortLabel}</option>)}</select></label>
            <label><span>Segmenta per</span><select value={segment} onChange={(event) => setSegment(event.target.value)}><option value="">Nessuna segmentazione</option>{dataset.fields.filter((field) => field.kind === 'categorical' && field.id !== dimension).map((field) => <option key={field.id} value={field.id}>{field.shortLabel}</option>)}</select></label>
            <label className="sa-explorer-limit"><span>Mostra nel grafico</span><select value={chartLimit} onChange={(event) => setChartLimit(event.target.value)}><option value="10">Top 10</option><option value="25">Top 25</option><option value="50">Top 50</option><option value="all">Tutti</option></select></label>
            <div className="sa-chart-switch"><button className={chartType === 'bars' ? 'active' : ''} onClick={() => setChartType('bars')}><i className="fas fa-chart-bar" /></button><button className={chartType === 'donut' ? 'active' : ''} onClick={() => setChartType('donut')} disabled={Boolean(segment)}><i className="fas fa-chart-pie" /></button></div>
          </section>

          <Panel eyebrow={fieldMap.get(dimension)?.category} title={fieldMap.get(dimension)?.shortLabel} action={<span className="sa-panel-stat"><b>{selectedDistribution.length}</b> valori distinti · <b>{selectedAnswerTotal}</b> selezioni</span>}>
            {segment ? <SegmentedBars items={segmented.items} segmentLabels={segmented.labels} total={selectedAnswerTotal || 1} /> : chartType === 'donut' ? <DonutChart items={searchedDistribution} total={selectedAnswerTotal} /> : <HorizontalBars items={chartDistribution} total={selectedAnswerTotal} limit={chartDistribution.length} />}
          </Panel>

          <Panel eyebrow="Long tail" title="Tutti i valori, senza tagli" action={<span className="sa-panel-stat"><b>{searchedDistribution.length}</b> visualizzati su {selectedDistribution.length}</span>}>
            <div className="sa-distribution-search sa-search"><i className="fas fa-magnifying-glass" /><input value={explorerSearch} onChange={(event) => setExplorerSearch(event.target.value)} placeholder={`Cerca tra tutti i valori di ${fieldMap.get(dimension)?.shortLabel.toLocaleLowerCase('it')}…`} />{explorerSearch && <button onClick={() => setExplorerSearch('')}><i className="fas fa-xmark" /></button>}</div>
            <div className="sa-distribution-list">
              {searchedDistribution.map((item) => (
                <article key={item.label}>
                  <span className="sa-distribution-rank">#{selectedDistribution.indexOf(item) + 1}</span>
                  <strong title={item.label}>{item.label}</strong>
                  <span>{formatNumber(item.count)} <small>{selectedAnswerTotal ? formatNumber((item.count / selectedAnswerTotal) * 100, 1) : 0}%</small></span>
                  <button onClick={() => { if (!filters.some((filter) => filter.field === dimension && filter.value === item.label)) setFilters((current) => [...current, { field: dimension, value: item.label }]) }} title="Usa come filtro"><i className="fas fa-filter" /></button>
                </article>
              ))}
              {!searchedDistribution.length && <EmptyState title="Nessun valore trovato" text="Prova una ricerca differente." />}
            </div>
          </Panel>

          <div className="sa-explorer-summary">
            <article><span>Valore leader</span><strong>{selectedDistribution[0]?.label ?? '—'}</strong><p>{selectedDistribution[0] ? `${selectedDistribution[0].count} selezioni` : 'Nessun dato'}</p></article>
            <article><span>Concentrazione top 3</span><strong>{selectedAnswerTotal ? formatNumber((selectedDistribution.slice(0, 3).reduce((sum, item) => sum + item.count, 0) / selectedAnswerTotal) * 100, 1) : 0}%</strong><p>quota dei primi tre valori</p></article>
            <article><span>Copertura</span><strong>{formatNumber((selectedAnswerTotal / Math.max(filteredRows.length, 1)) * 100, 1)}%</strong><p>selezioni per rispondente</p></article>
          </div>
        </div>
      )}

      {tab === 'quality' && (
        <div className="sa-view">
          <section className="sa-quality-intro">
            <div><span className="sa-overline"><i className="fas fa-shield-halved" /> CSV sempre intatto</span><h2>Laboratorio di normalizzazione</h2><p>Le unioni cambiano solo i conteggi visualizzati in questa sessione. I suggerimenti fuzzy richiedono conferma.</p></div>
            <label className="sa-toggle"><input type="checkbox" checked={exactMerge} onChange={(event) => setExactMerge(event.target.checked)} /><span /><div><b>Merge sicuri</b><small>maiuscole, spazi, accenti e punteggiatura</small></div></label>
            <label className="sa-toggle"><input type="checkbox" checked={entityMerge} onChange={(event) => setEntityMerge(event.target.checked)} /><span /><div><b>Normalizzazione semantica</b><small>nomi, programmi, città e professioni</small></div></label>
          </section>

          <section className="sa-quality-layout">
            <aside className="sa-quality-fields">
              <h3>Campi aperti</h3>
              {dataset.fields.filter((field) => MERGEABLE.includes(field.id)).map((field) => (
                <button key={field.id} className={qualityField === field.id ? 'active' : ''} onClick={() => { setQualityField(field.id); setQualitySearch('') }}>
                  <span>{field.shortLabel}<small>{field.profile.distinct} valori originali</small></span>
                  <b>{field.profile.exactGroups.length + field.profile.fuzzySuggestions.length}</b>
                </button>
              ))}
              <div className="sa-quality-note"><i className="fas fa-quote-left" /><p>“Cosa migliorare” è testo narrativo: viene analizzato, ma escluso dai merge per non alterarne il significato.</p></div>
            </aside>

            <div className="sa-quality-main">
              <div className="sa-quality-head">
                <div><span>{quality?.category}</span><h2>{quality?.shortLabel}</h2><p>{quality?.profile.distinct} valori grezzi · {quality?.profile.exactGroups.length} gruppi sicuri · {quality?.profile.fuzzySuggestions.length} proposte fuzzy</p></div>
                <div className="sa-search sa-search--small"><i className="fas fa-magnifying-glass" /><input value={qualitySearch} onChange={(event) => setQualitySearch(event.target.value)} placeholder="Cerca variante…" /></div>
              </div>

              <div className="sa-quality-section">
                {quality?.profile.entityModel?.groups?.length > 0 && (
                  <>
                    <header><div><h3>Valori riconosciuti</h3><p>Raggruppamento semantico specifico per questo campo.</p></div><span className="sa-confidence sa-confidence--entity">Entity-aware</span></header>
                    <div className="sa-entity-grid">
                      {quality.profile.entityModel.groups.filter((group) => !qualitySearch || group.canonical.toLocaleLowerCase('it').includes(qualitySearch.toLocaleLowerCase('it')) || group.variants.some((variant) => variant.value.toLocaleLowerCase('it').includes(qualitySearch.toLocaleLowerCase('it')))).map((group) => (
                        <article key={group.canonical}>
                          <div><i className="fas fa-user-check" /><span><strong>{group.canonical}</strong><small>{group.variants.length} forme riconosciute</small></span><b>{group.count}</b></div>
                          <p title={group.variants.map((variant) => variant.value).join(' · ')}>{group.variants.map((variant) => variant.value).join(' · ')}</p>
                        </article>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="sa-quality-section">
                <header><div><h3>Dizionario completo</h3><p>Ogni risposta originale e il valore usato nei conteggi: nessuna voce viene nascosta.</p></div><span className="sa-confidence sa-confidence--entity">{qualityOptionsVisible.length} valori</span></header>
                <div className="sa-dictionary-list">
                  {qualityOptionsVisible.map((option) => {
                    const targets = valuesFor({ [qualityField]: option.value }, qualityField)
                    const changed = targets.join(' · ') !== option.value
                    return <article key={option.value}><b>{option.count}</b><span title={option.value}>{option.value}</span><i className="fas fa-arrow-right" /><strong className={changed ? 'changed' : ''}>{targets.join(' · ') || option.value}</strong></article>
                  })}
                </div>
              </div>

              <div className="sa-quality-section">
                <header><div><h3>Merge deterministici</h3><p>Stessa parola dopo la normalizzazione tecnica.</p></div><span className="sa-confidence sa-confidence--safe">Sicuro</span></header>
                <div className="sa-merge-list">
                  {(quality?.profile.exactGroups ?? []).length ? quality.profile.exactGroups.map((group) => (
                    <article className="sa-merge-card" key={group.id}>
                      <div className="sa-merge-target"><i className="fas fa-check" /><span>Unifica in</span><strong>{group.canonical}</strong><b>{group.count}</b></div>
                      <div className="sa-variants">{group.variants.map((variant) => <span key={variant.value}>{variant.value}<b>{variant.count}</b></span>)}</div>
                    </article>
                  )) : <EmptyState title="Nessuna variante tecnica" text="Questo campo non presenta differenze solo di case o spazi." />}
                </div>
              </div>

              <div className="sa-quality-section">
                <header><div><h3>Suggerimenti intelligenti</h3><p>Possibili refusi: controlla sempre prima di accettare.</p></div><span className="sa-confidence">Revisione manuale</span></header>
                <div className="sa-fuzzy-list">
                  {fuzzyVisible.length ? fuzzyVisible.map((suggestion) => {
                    const accepted = acceptedFuzzy.has(suggestion.id)
                    return (
                      <article key={suggestion.id} className={accepted ? 'accepted' : ''}>
                        <div className="sa-fuzzy-score"><strong>{suggestion.confidence}%</strong><span>confidenza</span></div>
                        <div className="sa-fuzzy-values"><span>{suggestion.from}<b>{suggestion.fromCount}</b></span><i className="fas fa-arrow-right" /><span>{suggestion.to}<b>{suggestion.toCount}</b></span></div>
                        <button onClick={() => toggleFuzzy(suggestion.id)}><i className={`fas ${accepted ? 'fa-check' : 'fa-plus'}`} />{accepted ? 'Accettato' : 'Unisci'}</button>
                      </article>
                    )
                  }) : <EmptyState title="Nessun suggerimento" text="Nessuna coppia supera la soglia di somiglianza." />}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'records' && (
        <div className="sa-view">
          <section className="sa-table-tools">
            <div><strong>{formatNumber(filteredRows.length)} risposte</strong><span>{visibleFields.length} colonne visibili</span></div>
            <button onClick={() => setColumnsOpen((value) => !value)}><i className="fas fa-table-columns" /> Campi <b>{visibleFields.length}</b></button>
            <button onClick={exportRows}><i className="fas fa-file-export" /> Esporta CSV</button>
          </section>

          {columnsOpen && (
            <section className="sa-column-picker">
              <header><div><h3>Campi da visualizzare</h3><p>La selezione viene usata anche nell’export.</p></div><button onClick={() => setColumnsOpen(false)}><i className="fas fa-xmark" /></button></header>
              {['Ascolto', 'Percezione', 'Contenuti', 'Commerciale', 'Profilo'].map((category) => <div key={category}><strong>{category}</strong><div>{dataset.fields.filter((field) => field.category === category).map((field) => <label key={field.id}><input type="checkbox" checked={selectedColumns.has(field.id)} onChange={() => toggleColumn(field.id)} /><span>{field.shortLabel}</span></label>)}</div></div>)}
            </section>
          )}

          <section className="sa-table-wrap">
            <table>
              <thead><tr><th>#</th>{visibleFields.map((field) => <th key={field.id}><button onClick={() => setSort((current) => ({ field: field.id, direction: current.field === field.id && current.direction === 'asc' ? 'desc' : 'asc' }))}>{field.shortLabel}<i className={`fas ${sort.field === field.id ? (sort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down') : 'fa-sort'}`} /></button></th>)}</tr></thead>
              <tbody>{visibleRows.map((row) => <tr key={row.id}><td>{row.id}</td>{visibleFields.map((field) => <td key={field.id} title={row[field.id]}>{field.kind === 'rating' && row[field.id] ? <span className="sa-rating-cell">{row[field.id]}</span> : field.kind === 'multi' || MERGEABLE.includes(field.id) ? <div className="sa-cell-tags">{valuesFor(row, field.id).slice(0, 4).map((value) => <span key={value}>{value}</span>)}{valuesFor(row, field.id).length > 4 && <b>+{valuesFor(row, field.id).length - 4}</b>}</div> : normalize(field.id, row[field.id]) || <em>—</em>}</td>)}</tr>)}</tbody>
            </table>
            {!visibleRows.length && <EmptyState />}
          </section>
          <footer className="sa-pagination"><span>Pagina {page} di {pageCount}</span><div><button onClick={() => setPage(1)} disabled={page === 1}><i className="fas fa-angles-left" /></button><button onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1}><i className="fas fa-angle-left" /></button><button onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={page === pageCount}><i className="fas fa-angle-right" /></button><button onClick={() => setPage(pageCount)} disabled={page === pageCount}><i className="fas fa-angles-right" /></button></div></footer>
        </div>
      )}
    </main>
  )
}
